import { NormalizedNumber, Percentage, raise } from '@sparkdotfi/common-universal'
import BigNumber from 'bignumber.js'

import { getChainConfigEntry } from '@/config/chain'
import { TokenWithValue } from '@/domain/common/types'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { MarketInfo } from '@/features/sparklend/common/data/market-info/marketInfo'

export interface LiquidationDetails {
  liquidationPrice: NormalizedNumber
  tokenWithPrice: {
    priceInUSD: NormalizedNumber
    symbol: TokenSymbol
  }
}

export interface GetLiquidationDetailsParams {
  collaterals: TokenWithValue[]
  borrows: TokenWithValue[]
  marketInfo: MarketInfo
  liquidationThreshold: Percentage
}

export function getLiquidationDetails({
  collaterals,
  borrows,
  marketInfo,
  liquidationThreshold,
}: GetLiquidationDetailsParams): LiquidationDetails | undefined {
  const { oracles } =
    getChainConfigEntry(marketInfo.chainId).sparkLend ?? raise('SparkLend config is not defined on this chain')

  const hasOnlyFixedPriceBorrows =
    borrows.length > 0 && borrows.every((borrow) => oracles[borrow.token.symbol]?.type === 'fixed')

  if (!hasOnlyFixedPriceBorrows) {
    return undefined
  }
  const borrowInUsd = borrows.reduce((sum, borrow) => sum.plus(borrow.token.toUSD(borrow.value)), NormalizedNumber(0))

  const allCollateralsETHCorrelated = collaterals.every((collateral) =>
    ['WETH', 'wstETH', 'rETH', 'weETH', 'ezETH', 'rsETH'].includes(collateral.token.symbol),
  )
  const WETHPrice = marketInfo.findBySymbol(TokenSymbol('WETH'))?.unitPriceUsd
  if (allCollateralsETHCorrelated && WETHPrice) {
    const totalCollateralInWETH = collaterals.reduce((sum, collateral) => {
      const collateralPrice = marketInfo.findOneBySymbol(collateral.token.symbol).unitPriceUsd
      return sum.plus(collateral.value.times(collateralPrice).div(WETHPrice))
    }, NormalizedNumber.ZERO)
    const liquidationPrice = calculateLiquidationPrice({
      borrowInUsd,
      depositAmount: totalCollateralInWETH,
      liquidationThreshold,
    })

    return {
      liquidationPrice,
      tokenWithPrice: {
        priceInUSD: WETHPrice,
        symbol: TokenSymbol('ETH'),
      },
    }
  }

  if (collaterals.length !== 1) {
    return undefined
  }

  const collateral = collaterals[0]!
  const collateralPrice = collateral.token.unitPriceUsd

  const liquidationPrice = calculateLiquidationPrice({
    borrowInUsd,
    depositAmount: collateral.value,
    liquidationThreshold,
  })

  return {
    liquidationPrice,
    tokenWithPrice: {
      priceInUSD: collateralPrice,
      symbol: collateral.token.symbol,
    },
  }
}

interface CalculateLiquidationPriceArguments {
  borrowInUsd: NormalizedNumber
  depositAmount: NormalizedNumber
  liquidationThreshold: Percentage
}

function calculateLiquidationPrice({
  borrowInUsd,
  depositAmount,
  liquidationThreshold,
}: CalculateLiquidationPriceArguments): NormalizedNumber {
  const denominator = depositAmount.times(liquidationThreshold.decimalPlaces(4, BigNumber.ROUND_DOWN))
  if (denominator.isZero()) {
    return NormalizedNumber.ZERO
  }

  return borrowInUsd.div(denominator)
}
